import React from 'react';
import classNames from 'classnames';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import { type NormalizedCategory } from '@wix/communities-blog-client-common';
import {
  CategoryHeaderLayout,
  ImageAlignment,
  type TagName,
} from '@app/common/settings-components/types';
import { type AlignmentValue, getTextAlignmentValue } from '@app/common/style-params';
import stylesParams from '@app/components/Blog/stylesParams';
import { Image } from '@app/external/common/components/image/image';
import { isAllPostsCategory } from './all-posts-category';
import css from './category-header.scss';
import LegacyCategoryHeader from './legacy-category-header';

export type CategoryHeaderCategory = Pick<
  NormalizedCategory,
  'id' | 'label' | 'menuLabel' | 'description'
> & {
  cover?: NormalizedCategory['cover'] | null;
};

type Props = {
  category?: CategoryHeaderCategory | null;
  /**
   * Bridges the deprecated `categoryHeader-isEnabled` app setting: legacy sites
   * that turned the old category-page header on keep rendering that header, in
   * its original design, while the `categoryHeader-show` style param is unset.
   * Switching them to the new design would change their live site, so the new
   * header only takes over once the param is explicitly on.
   */
  legacyEnabled?: boolean;
};

const CategoryHeader = ({ category, legacyEnabled }: Props) => {
  const styles = useStyles();
  const { isRTL } = useEnvironment();

  if (!category) {
    return null;
  }

  const isEnabled = styles.get(stylesParams.showCategoryHeader);

  if (!isEnabled) {
    return legacyEnabled ? <LegacyCategoryHeader category={category} /> : null;
  }

  const displayAllPosts = styles.get(stylesParams.categoryHeaderDisplayAllPosts);

  if (isAllPostsCategory(category) && !displayAllPosts) {
    return null;
  }

  const layout = styles.get(stylesParams.categoryHeaderLayout) as CategoryHeaderLayout;
  const showImage = styles.get(stylesParams.showCategoryHeaderImage) && Boolean(category.cover);
  const title = category.label || category.menuLabel;
  const showTitle = styles.get(stylesParams.showCategoryHeaderTitle) && Boolean(title);
  const showDescription =
    styles.get(stylesParams.showCategoryHeaderDescription) && Boolean(category.description);

  if (!showImage && !showTitle && !showDescription) {
    return null;
  }

  const titleFont = styles.get(stylesParams.categoryHeaderTitleFont);
  // `htmlTag` is not declared on the font style param but is present at runtime.
  const TitleTag = ((titleFont as { htmlTag?: string } | undefined)?.htmlTag || 'h1') as TagName;

  const alignment = styles.get(stylesParams.categoryHeaderAlignment) as AlignmentValue;
  const imageFirst = styles.get(stylesParams.categoryHeaderImageFirst);
  const imageAlignment = styles.get(stylesParams.categoryHeaderImageAlignment) as ImageAlignment;
  // Side-by-side reserves a column for the image, so without a cover it must
  // fall back to the plain column to avoid an empty gap. Overlay keeps its
  // treatment coverless too: the scrim color becomes a solid band behind the
  // text, mirroring the Blocks widget's All Posts header.
  const isOverlay = layout === CategoryHeaderLayout.Overlay;
  const isSideBySide = showImage && layout === CategoryHeaderLayout.SideBySide;

  const rootClassName = classNames(css.container, {
    [css.classic]: layout === CategoryHeaderLayout.Classic,
    [css.overlay]: isOverlay,
    [css.overlayNoImage]: isOverlay && !showImage,
    [css.sideBySide]: isSideBySide,
    [css.contentFirst]: layout === CategoryHeaderLayout.Classic && !imageFirst,
    [css.imageEnd]: isSideBySide && imageAlignment === ImageAlignment.End,
  });

  return (
    <div
      className={rootClassName}
      data-hook="category-header"
      data-layout={layout}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={
        {
          '--category-header-content-width': `${styles.get(
            stylesParams.categoryHeaderContentWidth,
          )}%`,
          '--category-header-image-height': `${styles.get(
            stylesParams.categoryHeaderImageHeight,
          )}px`,
          '--category-header-padding-top': `${styles.get(stylesParams.categoryHeaderPaddingTop)}px`,
          '--category-header-padding-bottom': `${styles.get(
            stylesParams.categoryHeaderPaddingBottom,
          )}px`,
          '--category-header-side-padding': `${styles.get(
            stylesParams.categoryHeaderSidePadding,
          )}px`,
          '--category-header-title-description-gap': `${styles.get(
            stylesParams.categoryHeaderSpacingTitleDescription,
          )}px`,
          '--category-header-image-proportions': `${styles.get(
            stylesParams.categoryHeaderImageProportions,
          )}%`,
          '--category-header-text-align': getTextAlignmentValue(alignment, isRTL),
        } as React.CSSProperties
      }
    >
      {showImage ? (
        <div className={css.imageWrap} data-hook="category-header-image">
          <Image className={css.image} image={category.cover} />
        </div>
      ) : null}
      {(showTitle || showDescription) && (
        <div className={css.content}>
          {showTitle ? (
            <TitleTag className={css.title} data-hook="category-header-title">
              {title}
            </TitleTag>
          ) : null}
          {showDescription ? (
            <p className={css.description} data-hook="category-header-description">
              {category.description}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CategoryHeader;
